import React, { useRef, useEffect, useState } from 'react';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';

const EarthingForm = () => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(true);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      visual_checks: Array(12).fill({ status: '', remark: '' }),
      physical_checks: Array(2).fill({ status: '', remark: '' }),
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_other_comments: '',
      l1_status: 'draft',
      earth_pits_healthy: '',
      welding_ok: '',
      testing_commissioning: '',
      earthing_resistance_test: '',
      instruments: [{ serial: '', range: '', calibration: '' }],
      earthing_tests: Array(25).fill({ pit_no: '', purpose: '', value: '' }),
      observations: { a: '', b: '', c: '' },
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_other_comments: '',
      l2_status: 'draft',
    },
  });

  const formValues = useWatch({ control });

  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!isEditable) return;
      const payload = {
        form_data: {
          status: data.l1_status === 'draft' || data.l2_status === 'draft' ? 'draft' : 'pending',
          form_id: formId,
          formName: 'earthing',
          station_id: data.station_id,
          project_name: data.project_name,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            other_comments: data.l1_other_comments,
            status: data.l1_status,
          },
          l2: {
            earth_pits_healthy: data.earth_pits_healthy,
            welding_ok: data.welding_ok,
            testing_commissioning: data.testing_commissioning,
            earthing_resistance_test: data.earthing_resistance_test,
            instruments: data.instruments,
            earthing_tests: data.earthing_tests,
            observations: data.observations,
            comments: data.l2_comments,
            other_comments: data.l2_other_comments,
            status: data.l2_status,
          },
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
      autoSave(formValues);
  }, [formValues]);

  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'earthing' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setIsEditable(!['pending', 'approved'].includes(data.status));
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('visual_checks', Array.isArray(data.l1?.visual_checks)
        ? data.l1.visual_checks
        : Array(12).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(data.l1?.physical_checks)
        ? data.l1.physical_checks
        : Array(2).fill({ status: '', remark: '' }));
      setValue('l1_comments', data.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('earth_pits_healthy', data.l2?.earth_pits_healthy || '');
      setValue('welding_ok', data.l2?.welding_ok || '');
      setValue('testing_commissioning', data.l2?.testing_commissioning || '');
      setValue('earthing_resistance_test', data.l2?.earthing_resistance_test || '');
      setValue('instruments', Array.isArray(data.l2?.instruments)
        ? data.l2.instruments
        : [{ serial: '', range: '', calibration: '' }]);
      setValue('earthing_tests', Array.isArray(data.l2?.earthing_tests)
        ? data.l2.earthing_tests
        : Array(25).fill({ pit_no: '', purpose: '', value: '' }));
      setValue('observations', data.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_comments', data.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_status', data.l2?.status || 'draft');
    }
  }, [viewFormData, stationId, setValue]);

  const handleImageUpload = (e, level, role) => {
    if (!isEditable) return;
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, role);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`${level}_comments.${role}.signature`, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, role, type) => {
    if (!isEditable) return;
    setValue(`${level}_comments.${role}.signatureType`, type);
    setValue(`${level}_comments.${role}.signature`, '');
    if (type === 'digital') {
      setCurrentSignature({ level, role });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, role) => {
    if (!isEditable) return;
    setValue(`${level}_comments.${role}.signature`, '');
    setValue(`${level}_comments.${role}.signatureType`, 'upload');
  };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${currentSignature.level}_comments.${currentSignature.role}.signature`, signatureData);
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    if (!isEditable) return;
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    if (!isEditable) {
      setShowModal(false);
      return;
    }
    handleResetSignature(currentSignature.level, currentSignature.role);
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const onSubmit = (level, status) => (data) => {
    if (!isEditable) {
      toast.info('Form is not editable in pending or approved status');
      return;
    }
    const l1_status = level === 'l1' ? status : data.l1_status;
    const l2_status = level === 'l2' ? status : data.l2_status;
    const common_status = l1_status === 'draft' || l2_status === 'draft' ? 'draft' : 'pending';
    const payload = {
      form_data: {
        status: common_status,
        form_id: formId,
        formName: 'earthing',
        station_id: data.station_id,
        project_name: data.project_name,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          other_comments: data.l1_other_comments,
          status: l1_status,
        },
        l2: {
          earth_pits_healthy: data.earth_pits_healthy,
          welding_ok: data.welding_ok,
          testing_commissioning: data.testing_commissioning,
          earthing_resistance_test: data.earthing_resistance_test,
          instruments: data.instruments,
          earthing_tests: data.earthing_tests,
          observations: data.observations,
          comments: data.l2_comments,
          other_comments: data.l2_other_comments,
          status: l2_status,
        },
      },
    };
    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(`Level ${level.toUpperCase()} ${status === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">Earthing - Site Acceptance Testing</h2>
      {!isEditable && (
        <div className="alert alert-warning mb-4">
          This form is in View Mode Only.
        </div>
      )}
      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isEditable} />
            </Form.Group>
          </Col>
        </Row>

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title="Level 1 - Pre-Commissioning">
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Is the earthing installation in accordance with IS 3043, the approved earthing drawing, and specifications?',
                  'Is all earthing material as per the design parameters and specifications?',
                  'Does the size and rating of the earthing conductor comply with the specifications and standards?',
                  'Has the Earth Material TC been checked?',
                  'Is the painting of joints in Copper and GI strips done as per requirements?',
                  'Is the main earth loop laid as per IS 3043 at a depth of 600 mm below grade level?',
                  'Is the earthing pit covered with RCC/Cast Iron/HDPE cover as indicated in the drawing?',
                  'Are the various earth connections tightened properly, and are the bolts of correct size and quality?',
                  'Is the instruction board of the earth pit installed on the pit cover?',
                  'Are all instrumentation/electronic devices earthed to a separate earth pit (i.e., electronics earth pit)?',
                  'Is the earthing disconnection facility provided for checking the earth resistance of individual treated earth pits, and is the pit chamber size adequate for working on the earthing connections?',
                  'Is the clean earth connection insulated and connected to a separate earth electrode where applicable?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="Yes"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="No"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="NA"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`visual_checks.${index}.remark`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Is the earth strip/cable properly tightened with the earth and equipment?',
                  'Are the correct methods used for cable termination and tagging?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="Yes"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="No"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="NA"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`physical_checks.${index}.remark`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                      <td key={role}>
                        {field === 'Date' ? (
                          <Form.Control
                            type="date"
                            {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        ) : field === 'Signature' ? (
                          <div>
                            <Form.Select
                              {...register(`l1_comments.${role}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l1', role, e.target.value)}
                              className="mb-2"
                              disabled={!isEditable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {getValues(`l1_comments.${role}.signatureType`) === 'upload' && (
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'l1', role)}
                                className="mb-2"
                                disabled={!isEditable}
                              />
                            )}
                            {getValues(`l1_comments.${role}.signature`) && (
                              <div>
                                <img
                                  src={getValues(`l1_comments.${role}.signature`)}
                                  alt="Signature"
                                  style={{ maxWidth: '100px', marginTop: '5px' }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleResetSignature('l1', role)}
                                  className="mt-2"
                                  disabled={!isEditable}
                                >
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Form.Control
                            type="text"
                            {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">5. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l1_other_comments')}
                disabled={!isEditable}
                placeholder="Enter any additional comments or remarks for Level 1"
              />
            </Form.Group>

            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('l1', 'pending'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Submit Level 1
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('l1', 'draft'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Save As Draft
            </Button>
          </Tab>

          <Tab eventKey="level2" title="Level 2 - Commissioning">
            <h3 className="mt-4">6. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>All Earth Pits Found Healthy & Connected to System</Form.Label>
                  <Form.Select {...register('earth_pits_healthy')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Ensure All Weldings Are OK</Form.Label>
                  <Form.Select {...register('welding_ok')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Earthing Resistance Test</Form.Label>
                  <Form.Select {...register('earthing_resistance_test')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">7. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Instrument Description</th>
                  <th>Instrument Serial No.</th>
                  <th>Range</th>
                  <th>Calibration Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Earth Tester</td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register('instruments.0.serial')}
                      disabled={!isEditable}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register('instruments.0.range')}
                      disabled={!isEditable}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register('instruments.0.calibration')}
                      disabled={!isEditable}
                    />
                  </td>
                </tr>
              </tbody>
            </Table>

            <h3 className="mt-4">8. Earthing Resistance Test Max. value less then 5Ω</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Pit No.</th>
                  <th>Purpose</th>
                  <th>Value (Ω)</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 25 }, (_, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        defaultValue={`EP-${index + 1}`}
                        {...register(`earthing_tests.${index}.pit_no`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`earthing_tests.${index}.purpose`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`earthing_tests.${index}.value`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">9. Observations during Commissioning / Testing</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control
                  type="text"
                  {...register(`observations.${key}`)}
                  disabled={!isEditable}
                />
              </Form.Group>
            ))}

            <h3 className="mt-4">10. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                      <td key={role}>
                        {field === 'Date' ? (
                          <Form.Control
                            type="date"
                            {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        ) : field === 'Signature' ? (
                          <div>
                            <Form.Select
                              {...register(`l2_comments.${role}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l2', role, e.target.value)}
                              className="mb-2"
                              disabled={!isEditable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {getValues(`l2_comments.${role}.signatureType`) === 'upload' && (
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'l2', role)}
                                className="mb-2"
                                disabled={!isEditable}
                              />
                            )}
                            {getValues(`l2_comments.${role}.signature`) && (
                              <div>
                                <img
                                  src={getValues(`l2_comments.${role}.signature`)}
                                  alt="Signature"
                                  style={{ maxWidth: '100px', marginTop: '5px' }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleResetSignature('l2', role)}
                                  className="mt-2"
                                  disabled={!isEditable}
                                >
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Form.Control
                            type="text"
                            {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">11. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_other_comments')}
                disabled={!isEditable}
                placeholder="Enter any additional comments or remarks for Level 2"
              />
            </Form.Group>

            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('l2', 'pending'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Submit Level 2
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('l2', 'draft'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Save As Draft
            </Button>
          </Tab>
        </Tabs>

        <Modal show={showModal && isEditable} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditable}>
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditable}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

export default EarthingForm;